import { Box, Button, Divider, Flex, HStack, TabPanel, Text, useToast } from "@chakra-ui/react";
import { useEffect } from "react";
import { formatDate } from "utils";
import {
  AICheckOutput,
  ActivityDetailsSources,
  ActivityDetailsUpdateAnswer,
  getStatusFromActivity,
} from "./components/AnswerActivityOneEvent";
import { StatusTag } from "./components/AnswerDetailActivity";
import issbAPI from "api/issb/issb";
import { LabelText, ValueText } from "components/text/TextTypes";
import { Sparkle } from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import { useContextualPrompts } from "hooks/query/prompt/useContextualPrompts";

/**
 * See https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=3481-50278&t=FJT8f8XlaR10eM6g-1
 */
export default function AIDraftTab({
  questionId,
  canSave,
  activity,
  applyActivity,
  isActive,
  setReadFlag: setReadFlagLocal,
  questionData,
  allQuestionDataByRef,
  tags,
  onRegenerateAnswer,
  sendToAIChat,
}) {
  //   console.log("AIDraftTab", activity?.id);
  const draftCompleteness = getStatusFromActivity("completeness", activity);
  const draftConfidence = getStatusFromActivity("confidence", activity);
  const { prompts: aiDraftPrompts } = useContextualPrompts({
    location: "ai-draft",
    stage: questionData?.stageType,
    completeness: draftCompleteness,
    confidence: draftConfidence,
  });
  const toast = useToast();
  const handleApply = () => {
    applyActivity(activity);
    toast({
      title: "AI answer applied",
      description: "The AI answer has been applied to the question.",
      status: "success",
    });
  };
  // set the read flag to true
  useEffect(() => {
    if (isActive && !activity?.readFlag) {
      setReadFlagLocal(true);
      issbAPI.updateActivitySetReadFlag(activity);
    }
  }, [isActive, activity, setReadFlagLocal]);

  return (
    <TabPanel px={0}>
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="xl"
        bg="white"
        overflow="hidden"
      >
        {/* Answer content */}
        <Box px={5} py={4}>
          <LabelText color="headingGrey" fontSize="body.xs">
            Date
          </LabelText>
          <ValueText>{formatDate(activity?.date, true, false)}</ValueText>
        </Box>

        <Box px={5} pb={4}>
          {(() => {
            const completeness = getStatusFromActivity("completeness", activity);
            const confidence = getStatusFromActivity("confidence", activity);
            return (completeness || confidence) ? (
              <Box mb={3}>
                <LabelText>Status</LabelText>
                <HStack>
                  {completeness && <StatusTag showField field="completeness" value={completeness} />}
                  {confidence && <StatusTag showField field="confidence" value={confidence} />}
                </HStack>
              </Box>
            ) : null;
          })()}

          <ActivityDetailsUpdateAnswer
            activity={activity}
            questionData={questionData}
            showOldAnswer={false}
            useEditorAsViewer={true}
            allQuestionDataByRef={allQuestionDataByRef}
            tags={tags}
            minimal={true}
          />

          <AICheckOutput activity={activity} />

          <ActivityDetailsSources activity={activity} questionData={questionData} />
        </Box>

        {/* Review section — contextual prompts from rules system */}
        {aiDraftPrompts.length > 0 && (
          <>
            <Divider borderColor="gray.200" />
            <Box px={5} py={3}>
              <HStack spacing={2} mb={3}>
                <Sparkle size={16} weight="fill" color={colors.limeShade[600]} />
                <Text fontSize="body.s" fontWeight="semibold" color="limeShade.600">
                  Review
                </Text>
              </HStack>
              <Flex gap={2} flexWrap="wrap">
                {aiDraftPrompts.map((p) => (
                  <Button
                    key={p.id}
                    variant="outline"
                    size="sm"
                    borderRadius="full"
                    borderColor="gray.300"
                    color="gray.600"
                    fontWeight="normal"
                    bg="white"
                    _hover={{ borderColor: "headingLime", color: "darkTeal" }}
                    onClick={() => sendToAIChat?.(p.content)}
                  >
                    {p.name}
                  </Button>
                ))}
              </Flex>
            </Box>
          </>
        )}
      </Box>

      <Button variant="primary" onClick={handleApply} disabled={!canSave} w="100%" mt={3}>
        {activity.draft ? "Apply Answer" : "Reapply Answer"}
      </Button>
    </TabPanel>
  );
}
